/**
 * useAccount — B10.2
 *
 * GET /api/v2/account/me: Eigenes User-Profil + Settings laden.
 */
import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'
import { useAuthStore } from '../../stores/authStore'
import { setDisplayPreferences } from '../../lib/locale'

/**
 * Story 0.2 — ein Hotspot-Kontingent (Doppel-Limit-Modell, Story 6.1).
 * `limit: null` = unbegrenzt (z. B. Plan "unlimited").
 */
export type HotspotQuotaBucket = {
  used: number
  limit: number | null
  /**
   * Story 6.7 (Issue #44/#67): eigene Hotspots dieses Buckets ohne
   * `user_hotspots`-Zeile — durchs Kontingent blockiert, bevor sie überhaupt
   * als `used` zählen. Optional, weil ältere BE-Deploys das Feld noch nicht
   * liefern.
   */
  blocked?: number
}

/** Story 0.2 — beide Kontingente fuer die Sidebar-Kontingent-Meter. */
export type HotspotQuota = {
  mobile: HotspotQuotaBucket
  iot: HotspotQuotaBucket
}

export type AccountData = {
  id: number
  email: string
  created_at: string
  is_premium: boolean
  premium_until: string | null
  plan: string
  time_zone: string
  currency: string
  theme: string
  /**
   * Issue #55 — gespeicherte Sprachpraeferenz (z.B. "de"/"en"). Optional
   * typisiert, damit aeltere Backend-Staende ohne dieses Feld nicht brechen.
   */
  locale?: string
  use_metric: boolean
  time_format: string
  date_format: string
  num_digits: number
  volume: number
  newsletter: boolean
  monthly_report: boolean
  hotspots_count: number
  wallets_count: number
  contacts_count: number
  /** Standortgeber (/hosts) — Bezugsgroesse der Settings-Kachel, siehe me_controller. */
  hosts_count: number
  /**
   * Issue #56 — EINDEUTIGE Personenzahl (Contact+Host ueber E-Mail/Name
   * gemergt, PeopleMergeService, gleiche Quelle wie GET /api/v2/people).
   * NICHT contacts_count + hosts_count bilden — das zaehlt dieselbe Person
   * doppelt, sobald sie beide Rollen hat (der Regelfall im Bestand).
   */
  people_count?: number
  api_key?: string | null
  /** R3-S3: maskierter API-Key ("abcd****...****wxyz") aus GET /api/v2/account/me */
  api_key_masked?: string | null
  /** R3-S3: Monatskontingent der Public API (0 = kein API-Zugang) */
  api_quota_limit?: number
  /** R3-S3: verbleibende Requests im laufenden Monat (Restzähler) */
  api_quota_remaining?: number
  /** R3-S3: hat der Tarif Public-API-Zugang (= api_quota_limit > 0) */
  api_access?: boolean
  /** Admin-Flag für Site-Management-Zugriff (S10-03) */
  is_admin?: boolean
  /** Zugehörige Site-ID für Whitelabel-User (S10-03) */
  site_id?: number | null
  /**
   * Story 0.2: Doppel-Kontingent (Mobile/IoT) für die beiden
   * Sidebar-Kontingent-Meter. Optional, damit ältere Backends nicht brechen.
   */
  hotspot_quota?: HotspotQuota

  // Notification Matrix
  notification_email_reward?: boolean
  notification_socket_reward?: boolean
  notification_push_reward?: boolean
  notification_telegram_reward?: boolean
  notification_webhook_reward?: boolean
  notification_email_mobile_genesis?: boolean
  notification_socket_mobile_genesis?: boolean
  notification_push_mobile_genesis?: boolean
  notification_telegram_mobile_genesis?: boolean
  notification_webhook_mobile_genesis?: boolean
  notification_email_payment?: boolean
  notification_socket_payment?: boolean
  notification_push_payment?: boolean
  notification_telegram_payment?: boolean
  notification_webhook_payment?: boolean
  notification_email_account?: boolean
  notification_socket_account?: boolean
  notification_push_account?: boolean
  notification_telegram_account?: boolean
  notification_webhook_account?: boolean
  notification_email_alert?: boolean
  notification_socket_alert?: boolean
  notification_push_alert?: boolean
  notification_telegram_alert?: boolean
  notification_webhook_alert?: boolean
  notification_email_beacon?: boolean
  notification_socket_beacon?: boolean
  notification_push_beacon?: boolean
  notification_telegram_beacon?: boolean
  notification_webhook_beacon?: boolean
  notification_email_witness?: boolean
  notification_socket_witness?: boolean
  notification_push_witness?: boolean
  notification_telegram_witness?: boolean
  notification_webhook_witness?: boolean
  notification_email_offline?: boolean
  notification_socket_offline?: boolean
  notification_push_offline?: boolean
  notification_telegram_offline?: boolean
  notification_webhook_offline?: boolean
  notification_email_online?: boolean
  notification_socket_online?: boolean
  notification_push_online?: boolean
  notification_telegram_online?: boolean
  notification_webhook_online?: boolean
  notification_email_no_heartbeat?: boolean
  notification_socket_no_heartbeat?: boolean
  notification_push_no_heartbeat?: boolean
  notification_telegram_no_heartbeat?: boolean
  notification_webhook_no_heartbeat?: boolean
  notification_email_first_heartbeat?: boolean
  notification_socket_first_heartbeat?: boolean
  notification_push_first_heartbeat?: boolean
  notification_telegram_first_heartbeat?: boolean
  notification_webhook_first_heartbeat?: boolean
  notification_email_reward_scale?: boolean
  notification_socket_reward_scale?: boolean
  notification_push_reward_scale?: boolean
  notification_telegram_reward_scale?: boolean
  notification_webhook_reward_scale?: boolean
  notification_email_listen?: boolean
  notification_socket_listen?: boolean
  notification_push_listen?: boolean
  notification_telegram_listen?: boolean
  notification_webhook_listen?: boolean
  // Paritaets-Runde 3 / Stream 2: restliche kanonische Events (broadcast.rb)
  notification_email_sync_start?: boolean
  notification_socket_sync_start?: boolean
  notification_push_sync_start?: boolean
  notification_telegram_sync_start?: boolean
  notification_webhook_sync_start?: boolean
  notification_email_sync_stop?: boolean
  notification_socket_sync_stop?: boolean
  notification_push_sync_stop?: boolean
  notification_telegram_sync_stop?: boolean
  notification_webhook_sync_stop?: boolean
  notification_email_ping?: boolean
  notification_socket_ping?: boolean
  notification_push_ping?: boolean
  notification_telegram_ping?: boolean
  notification_webhook_ping?: boolean
  notification_email_relay?: boolean
  notification_socket_relay?: boolean
  notification_push_relay?: boolean
  notification_telegram_relay?: boolean
  notification_webhook_relay?: boolean
  notification_socket_challenge?: boolean
  notification_push_challenge?: boolean
  notification_telegram_challenge?: boolean
  notification_webhook_challenge?: boolean
  notification_socket_premium?: boolean
  notification_push_premium?: boolean
  notification_telegram_premium?: boolean
  notification_webhook_premium?: boolean
  telegram_chat_id?: string | null
  telegram_connect_token?: string | null
  telegram_enabled?: boolean
  telegram_muted_until?: string | null
  fcm_token?: string | null
  fcm_enabled?: boolean

  // Guard-Einstellungen
  guard_online?: boolean
  guard_sync?: boolean
  guard_reward?: boolean
  guard_heartbeat?: boolean
  guard_ip?: boolean
  guard_v2?: boolean
  sync_treshold?: number
  heartbeat_treshold?: number

  // F5 (FIN-SWEEP-FE-7): BE liefert dashboard_layout unter account/me mit.
  // Wird von useDashboardLayout (F038) konsumiert — gemeinsamer Query-Key
  // mit useAccount, dedupliziert den GET /api/v2/account/me.
  dashboard_layout?: {
    widgets: Array<{
      id: 'kpi_strip' | 'rewards_chart' | 'top_hotspots' | 'network_health'
      label: string
      visible: boolean
    }>
  }
}

export type AccountUpdatePayload = Partial<
  Omit<
    AccountData,
    | 'id'
    | 'email'
    | 'created_at'
    | 'hotspots_count'
    | 'wallets_count'
    | 'contacts_count'
    | 'hosts_count'
    | 'api_key'
    | 'api_key_masked'
    | 'api_quota_limit'
    | 'api_quota_remaining'
    | 'api_access'
    | 'is_admin'
    | 'site_id'
  >
>

export const ACCOUNT_QUERY_KEY = ['account', 'me'] as const

// F5 (FIN-SWEEP-FE-7): Exportiert, damit useDashboardLayout denselben Key +
// dieselbe queryFn nutzen kann → gemeinsamer Cache, ein Request pro
// Page-Load statt zwei.
export async function fetchAccount(): Promise<AccountData> {
  const result = await apiClient.GET('/api/v2/account/me')
  if (result.error) throw result.error
  // F-ROBUST-1: Nie `undefined` zurückgeben — React Query verbietet das
  // ("Query data cannot be undefined"). Bei nicht-werfendem non-2xx (z.B. 401
  // ohne Body) ist result.data undefined → auf null normalisieren (null ist ein
  // gültiger Query-Wert; Consumer behandeln Abwesenheit via isLoading/isError).
  return (result.data ?? null) as AccountData
}

export function useAccount() {
  // AUTH-Block-Fix: nach Logout feuerte diese Query weiter (401 auf account/me),
  // weil queryClient.clear() beim Logout aktive Observer sofort neu fetchen
  // laesst (Cache-Eintrag entfernt -> TanStack Query holt ihn erneut), noch
  // bevor die Komponente durch die Navigation unmounted wird. `enabled` an
  // isAuthenticated koppeln verhindert diesen Request zuverlaessig.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const query = useQuery<AccountData>({
    queryKey: ACCOUNT_QUERY_KEY,
    queryFn: fetchAccount,
    retry: false,
    staleTime: 60_000,
    enabled: isAuthenticated,
  })

  // Issue #124 (= #55): zentraler Einspeisepunkt für die Anzeige-Einstellungen
  // (date_format/time_format/num_digits, siehe lib/locale.ts). useAccount ist
  // die einzige Stelle, die den Account laedt — jede Komponente, die
  // formatLocalDate/formatLocalDateTime/formatDecimal nutzt, bekommt die
  // Praeferenzen dadurch automatisch, ohne den Account selbst zu kennen.
  // Nach Logout (kein query.data mehr) auf "keine Einstellungen" zuruecksetzen,
  // damit ein nachfolgender Login eines anderen Kontos nicht mit den alten
  // Werten formatiert.
  useEffect(() => {
    setDisplayPreferences(
      query.data
        ? {
            date_format: query.data.date_format,
            time_format: query.data.time_format,
            num_digits: query.data.num_digits,
          }
        : null,
    )
  }, [query.data])

  return {
    account: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  }
}
